'use client';
import React, { useEffect, useState, useRef } from 'react';
import { auth } from '../../config/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserProjects, updateProject } from '../../services/projectService';
import serverConfig from '../../config/server-port.json';
import googleLogo from '../../assets/images/logo/google.png';
import { API_URL } from '../../services/storageService';

function Navbar() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const [profileImageError, setProfileImageError] = useState(false);
  const [profileImageLoaded, setProfileImageLoaded] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const navigate = useNavigate();

  // Function to get proxied image URL
  const getProxiedImageUrl = async (originalUrl) => {
    try {
      const credentials = btoa('tylersabino:QwErTy123!');
      const response = await fetch(`${API_URL}/proxy-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${credentials}`
        },
        body: JSON.stringify({ url: originalUrl })
      });
      
      if (!response.ok) {
        throw new Error('Failed to proxy image');
      }
      
      const data = await response.json();
      return data.url;
    } catch (error) {
      console.error('Error getting proxied image URL:', error);
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // console.log('Auth state changed:', user);
      if (user) {
        // console.log('User photo URL:', user.photoURL);
        // console.log('User email:', user.email);
        setProfileImageError(false);
        setProfileImageLoaded(false);
        setUser(user);
        
        // Try to get proxied image URL if photoURL exists
        if (user.photoURL) {
          const proxiedUrl = await getProxiedImageUrl(user.photoURL);
          if (proxiedUrl) {
            setProfileImageUrl(proxiedUrl);
          } else {
            setProfileImageError(true);
          }
        }
      } else {
        setUser(null);
        setProfileImageError(false);
        setProfileImageLoaded(false);
        setProfileImageUrl(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update progress based on active slide
  useEffect(() => {
    if (location.pathname === '/brands/new') {
      const handleSlideChange = (event) => {
        const activeIndex = event.detail;
        const totalSlides = 6; // Total number of onboarding slides
        const newProgress = ((activeIndex + 1) / totalSlides) * 100;
        setProgress(newProgress);
      };

      document.addEventListener('slideChange', handleSlideChange);
      return () => document.removeEventListener('slideChange', handleSlideChange);
    }
  }, [location.pathname]);

  // Add mobile check effect
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    checkMobile();
  }, []);

  const handleLogout = async () => {
    try {
      // console.log('Logging out...');
      await signOut(auth);
      // console.log('Logout successful');
      navigate('/');
    } catch (error) {
      // console.error('Logout error:', error);
      setError('Failed to logout. Please try again.');
    }
  };

  function handleScroll() {
    const bodyScroll = window.scrollY;
    const navbar = document.querySelector('.navbar');

    if (bodyScroll > 300) navbar.classList.add('nav-scroll');
    else navbar.classList.remove('nav-scroll');
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function handleToggleNav() {
    if (
      document
        .querySelector('.navbar .navbar-collapse')
        .classList.contains('show')
    ) {
      document
        .querySelector('.navbar .navbar-collapse')
        .classList.remove('show');
    } else if (
      !document
        .querySelector('.navbar .navbar-collapse')
        .classList.contains('show')
    ) {
      document.querySelector('.navbar .navbar-collapse').classList.add('show');
    }
  }
  
  const handleLinkClick = (e) => {
    setShowDropdown(false);
  };

  return (
    <nav className="navbar navbar-expand-lg bord blur" style={{ overflow: 'visible', padding: '6px 0' }}>
      <div className="container o-hidden" style={{ overflow: 'visible' }}>
        <a href="/" className="logo icon-img-100">
          <img src="/assets/imgs/logo1.svg" alt="logo" style={{ maxHeight: '40px', width: 'auto', marginBottom: '5px' }} />
        </a>

        <button
          className="navbar-toggler"
          type="button"
          data-toggle="collapse"
          data-target="#navbarSupportedContent"
          aria-controls="navbarSupportedContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
          onClick={handleToggleNav}
        >
          {/*<span className="icon-bar">
            <i className="fas fa-bars"></i>
          </span>*/}
        </button>
      
        <div
          className="collapse navbar-collapse justify-content-center"
          id="navbarSupportedContent"
          style={{ overflow: 'visible' }}
        >
          <ul className="navbar-nav">
            <li className="nav-item" style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
              {location.pathname === '/brands/new' && (
                <div className="progress-bar" style={{ width: '300px', height: '4px', background: 'rgba(128, 128, 128, 0.2)', borderRadius: '2px', overflow: 'hidden' }}>
                  <div className="progress" style={{ width: `${progress}%`, height: '100%', background: '#fd5b38', transition: 'width 0.3s ease' }}></div>
                </div>
              )}
            </li>
          </ul>
        </div>

        <div className="contact-button" style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'visible' }}>
          {!loading && (
            <>  
              <>
                <a href="/brands/new" className="butn butn-sm butn-bg main-colorbg radius-5" style={{ padding: '6px 12px', lineHeight: '1.2', display: 'inline-flex', alignItems: 'center' }}>
                  <span className="text">{isMobile ? 'Start your BluePrint' : 'Start Your Brand BluePrint'}</span>
                </a>
                {user ? (
                  <div ref={dropdownRef} style={{ position: 'relative', zIndex: 99999, overflow: 'visible' }}>
                    <div
                      onClick={() => setShowDropdown(!showDropdown)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: '#fd5b38',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        overflow: 'hidden',
                        position: 'relative',
                        zIndex: 99999
                      }}
                    >
                      {profileImageUrl && !profileImageError ? (
                        <img 
                          src={profileImageUrl} 
                          alt="Profile" 
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: profileImageLoaded ? 'block' : 'none'
                          }}
                          onError={(e) => {
                            // console.error('Profile image failed to load:', {
                            //   url: profileImageUrl,
                            //   error: e
                            // });
                            setProfileImageError(true);
                            e.target.style.display = 'none';
                          }}
                          onLoad={() => {
                            // console.log('Profile image loaded successfully:', profileImageUrl);
                            setProfileImageLoaded(true);
                            setProfileImageError(false);
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#fd5b38',
                          color: 'white',
                          fontSize: '16px',
                          fontWeight: 'bold'
                        }}>
                          {user?.email?.[0]?.toUpperCase() || 'P'}
                        </div>
                      )}
                    </div>
                    {showDropdown && (
                      <div
                        style={{
                          position: 'fixed',
                          top: '80px',
                          right: '20px',
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          minWidth: '150px',
                          zIndex: 99999,
                          border: '1px solid rgba(255,255,255,0.1)'
                        }}
                      >
                        <a
                          className="nav-link"
                          href="/brands"
                          onClick={handleLinkClick}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '12px 16px',
                            color: 'white',
                            textDecoration: 'none',
                            fontSize: '14px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <span className="rolling-text">My Brands</span>
                        </a>
                        <a
                          className="nav-link"
                          href="/survey"
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '12px 16px',
                            color: 'white',
                            textDecoration: 'none',
                            fontSize: '14px',
                            transition: 'background-color 0.2s',
                            borderTop: '1px solid rgba(255,255,255,0.08)',
                            marginTop: 8
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <span className="rolling-text">Submit Feedback</span>
                        </a>
                        <button
                          onClick={handleLogout}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '12px 16px',
                            color: 'white',
                            background: 'none',
                            border: 'none',
                            textAlign: 'left',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div 
                    onClick={() => navigate('/sign-in')}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      position: 'relative',
                      zIndex: 99999,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <img 
                      src={googleLogo} 
                      alt="Sign in with Google" 
                      style={{
                        width: '18px',
                        height: '18px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
